// The four panels of the issuing page. Their shared furniture — the card, the
// skeleton, the mixed-units caveat, the delta line — is `panelChrome.tsx`, so
// adding a panel touches this file and adjusting the chrome touches that one.

import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { TypePill } from '@/components/synapse/TypePill';
import { MEASURES, MEASURE_IDS } from '@/lib/analytics';
import type { BucketGranularity, MeasureId } from '@/lib/analytics';
import { computeDelta, shareOfTotal } from '@/lib/issuingCharts';
import type { ChartMember } from '@/lib/issuingCharts';
import { isKnownItemType, translateUnit } from '@/lib/itemTypeConfig';
import type { ItemType } from '@/lib/itemTypeConfig';
import { cn } from '@/lib/utils';
import {
  DeltaLine,
  MixedUnitsNote,
  PanelSection,
  PanelSkeleton,
  type PanelProps,
} from './panelChrome';
import { MixBar } from './MixBar';
import { StackedColumnChart } from './StackedColumnChart';
import type { ColumnBucket } from './StackedColumnChart';
import { TopItemsList } from './TopItemsList';
import type { TopItemRow } from './TopItemsList';
import { itemTypeTone } from './chartTones';
import {
  chartMembers,
  dimensionSlice,
  formatMeasure,
  measureMetaOf,
  memberLabel,
  memberValues,
  mixesItemUnits,
  sumMeasure,
  timeBuckets,
} from './panelData';
import { spacedBucketLabels } from './issuingWindow';

// ---------------- headline ----------------

interface HeadlineTilesProps extends Omit<PanelProps, 'formatValue' | 'formatPercent'> {
  /** Length of the window, which is also the length of the comparison window. */
  days: number;
}

/**
 * Both measures as stat tiles, the active one first.
 *
 * The second tile is the *other* measure rather than a count of distinct
 * medicines: no measure in the contract counts distinct items, and the ranked
 * panel is truncated to ten, so a "medicines issued" figure derived from this
 * payload would be a guess wearing a number's clothes. Units and issues answer
 * different questions about the same window — a thousand units over twelve
 * issues is a different day from a thousand over three hundred — so the pair is
 * a real headline rather than a placeholder.
 *
 * The units tile carries its mixed-units note whichever measure is active: the
 * tile is on screen either way, and 28px of "250" under the word "Units" is
 * exactly the figure the caveat exists for.
 */
export function HeadlineTiles({ panels, measure, isLoading, days }: HeadlineTilesProps) {
  const { t, i18n } = useTranslation();
  const totals = panels?.panels.totals;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3" aria-label={t('analytics.headline')}>
        <PanelSkeleton height="h-[104px]" />
        <PanelSkeleton height="h-[104px]" />
      </div>
    );
  }

  // Active measure first: the tile the user chose to look at leads.
  const ordered: MeasureId[] = [measure, ...MEASURE_IDS.filter((id) => id !== measure)];

  return (
    <section className="grid grid-cols-2 gap-3" aria-label={t('analytics.headline')}>
      {ordered.map((id) => {
        const meta = measureMetaOf(totals, id) ?? MEASURES[id];
        const value = sumMeasure(totals?.rows, id);
        const delta = computeDelta(value, sumMeasure(totals?.comparison?.rows, id));
        return (
          <Card key={id} className="p-4">
            <div className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
              {t(meta.labelKey)}
            </div>
            <div className="mt-1 font-mono text-[28px] font-semibold tabular-nums text-ink">
              {formatMeasure(value, meta.format, i18n.language)}
            </div>
            <DeltaLine delta={delta} days={days} format={meta.format} />
            {mixesItemUnits(meta.denomination) && <MixedUnitsNote className="mt-1.5" />}
          </Card>
        );
      })}
    </section>
  );
}

// ---------------- trend ----------------

interface TrendPanelProps extends Omit<PanelProps, 'formatPercent'> {
  bucket: BucketGranularity;
  granularityLabel: string;
  measureLabel: string;
  from: string;
  to: string;
  /** How many of the columns may carry a date; the rest render unlabelled. */
  maxTicks: number;
}

/**
 * The centrepiece: one column per bucket, stacked by item type.
 *
 * The axis is whatever `members.time_bucket` the payload carries, which is the
 * complete set of buckets the window covers — quiet ones included. The page does
 * not construct it: a week bucket starts on its Monday, which can precede the
 * requested `from`, and only the server knows where the server put its
 * boundaries.
 */
export function TrendPanel({
  panels,
  measure,
  formatValue,
  isLoading,
  bucket,
  granularityLabel,
  measureLabel,
  from,
  to,
  maxTicks,
}: TrendPanelProps) {
  const { t, i18n } = useTranslation();

  if (isLoading) {
    return (
      <PanelSection title={t('analytics.trend.title')}>
        <PanelSkeleton height="h-48" />
      </PanelSection>
    );
  }

  const buckets = timeBuckets(panels?.panels.trend, 'item_type', measure);
  const labels = spacedBucketLabels(
    buckets.map((entry) => entry.id),
    bucket,
    i18n.language,
    maxTicks,
  );
  const columns: ColumnBucket[] = buckets.map((entry, i) => ({
    ...entry,
    label: labels[i] ?? '',
  }));

  return (
    <PanelSection title={t('analytics.trend.title')}>
      <StackedColumnChart
        buckets={columns}
        tone={itemTypeTone}
        formatValue={formatValue}
        ariaLabel={t('analytics.trend.aria', {
          measure: measureLabel,
          granularity: granularityLabel,
          from,
          to,
        })}
        emptyLabel={t('analytics.trend.empty')}
        partialNote={t('analytics.trend.partialNote')}
      />
    </PanelSection>
  );
}

// ---------------- by item type ----------------

interface ByTypePanelProps extends PanelProps {
  selectedTypes: readonly ItemType[];
  onSelectType: (type: ItemType) => void;
}

/**
 * The period split by item type: the mix bar with its legend, then a row per
 * type carrying both measures and its share.
 *
 * `shareOfTotal` is the right denominator here, unlike in the ranked list — this
 * panel returns every type, so its rows really do add up to the period.
 *
 * The rows always carry a units column, and a type's units are as mixed as the
 * items inside it, so the panel always carries the note that says so.
 */
export function ByTypePanel({
  panels,
  measure,
  formatValue,
  formatPercent,
  isLoading,
  selectedTypes,
  onSelectType,
}: ByTypePanelProps) {
  const { t, i18n } = useTranslation();
  const panel = panels?.panels.by_type;

  if (isLoading) {
    return (
      <PanelSection title={t('analytics.byType.title')}>
        <PanelSkeleton height="h-40" />
      </PanelSection>
    );
  }

  const segments = chartMembers(t, panel, 'item_type', measure);
  // Both measures, not just the active one: the per-type rows print units *and*
  // issues side by side, which is the comparison the mix bar above cannot make.
  const byMeasure = Object.fromEntries(
    MEASURE_IDS.map((id) => [id, chartMembers(t, panel, 'item_type', id)]),
  ) as Record<MeasureId, ChartMember[]>;
  const shares = shareOfTotal(segments.map((segment) => segment.value));

  return (
    <PanelSection title={t('analytics.byType.title')} action={<MixedUnitsNote />}>
      <MixBar
        segments={segments}
        tone={itemTypeTone}
        formatValue={formatValue}
        formatPercent={formatPercent}
        ariaLabel={t('analytics.byType.legend')}
        emptyLabel={t('analytics.byType.empty')}
      />

      {segments.length > 0 && (
        <ul
          aria-label={t('analytics.byType.aria')}
          className="mt-4 flex flex-col divide-y divide-line border-t border-line"
        >
          {segments.map((segment, i) => {
            const known = isKnownItemType(segment.id);
            const active = known && selectedTypes.includes(segment.id);
            return (
              <li key={segment.id}>
                <button
                  type="button"
                  disabled={!known}
                  aria-pressed={known ? active : undefined}
                  // The row is a pill and three numbers, so its name has to be
                  // supplied — and it has to say which way the click goes.
                  aria-label={
                    known
                      ? active
                        ? t('analytics.byType.clear')
                        : t('analytics.byType.filterTo', { type: segment.label })
                      : undefined
                  }
                  onClick={() => known && onSelectType(segment.id)}
                  className={cn(
                    'flex w-full items-center gap-2 px-1 py-2 text-left transition-colors',
                    known ? 'hover:bg-paper-2' : 'cursor-default',
                    active && 'bg-paper-2',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    {known ? (
                      <TypePill type={segment.id} size="sm" />
                    ) : (
                      // A type this build has never heard of still has to render.
                      <Badge tone="neutral" size="sm">
                        {segment.label}
                      </Badge>
                    )}
                  </span>
                  {MEASURE_IDS.map((id) => (
                    <span
                      key={id}
                      className="w-16 shrink-0 text-right font-mono text-[12px] tabular-nums text-ink-2"
                    >
                      {formatMeasure(
                        byMeasure[id][i]?.value ?? 0,
                        measureMetaOf(panel, id)?.format ?? MEASURES[id].format,
                        i18n.language,
                      )}
                    </span>
                  ))}
                  <span className="w-12 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-2">
                    {formatPercent(shares[i] ?? 0)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </PanelSection>
  );
}

// ---------------- top items ----------------

interface TopItemsPanelProps extends PanelProps {
  selectedItemId: string | null;
  onSelectItem: (itemId: string) => void;
  measureLabel: string;
}

/**
 * The ranked medicines, and the way into the specific-medicine filter.
 *
 * Share is computed against the `totals` panel, not against these rows: the
 * panel is truncated to its top ten server-side, so summing what arrived would
 * hand ten medicines exactly 100% of a period they are only part of.
 *
 * `chartMembers` is not used, and the reason is `member.unit` and
 * `member.sublabel`: a ranked row prints both and a `ChartMember` carries
 * neither. The values still fold through `memberValues`, so the two readings of
 * a panel cannot disagree about what a member's figure is.
 */
export function TopItemsPanel({
  panels,
  measure,
  formatValue,
  formatPercent,
  isLoading,
  selectedItemId,
  onSelectItem,
  measureLabel,
}: TopItemsPanelProps) {
  const { t } = useTranslation();
  const panel = panels?.panels.top_items;

  if (isLoading) {
    return (
      <PanelSection title={t('analytics.topItems.title')}>
        <PanelSkeleton height="h-40" />
      </PanelSection>
    );
  }

  const slice = dimensionSlice(panel, 'item');
  const values = memberValues(panel, 'item', measure);
  const meta = measureMetaOf(panel, measure) ?? MEASURES[measure];
  // A row's number is suffixed with the item's own unit only when the measure is
  // counted in it; a measure counted in occurrences is already named by the
  // measure toggle, and "20 tablets" under "Issues" would be a wrong reading.
  const inItemUnits = mixesItemUnits(meta.denomination);

  const rows: TopItemRow[] = slice.members.map((member) => {
    const unit = inItemUnits && member.unit ? translateUnit(t, member.unit) : undefined;
    return {
      id: member.id,
      label: memberLabel(t, member, member.id),
      ...(member.sublabel ? { sublabel: member.sublabel } : {}),
      value: values.get(member.id) ?? 0,
      ...(unit ? { unit } : {}),
    };
  });

  return (
    <PanelSection title={t('analytics.topItems.title')}>
      <TopItemsList
        rows={rows}
        total={sumMeasure(panels?.panels.totals?.rows, measure)}
        formatValue={formatValue}
        formatPercent={formatPercent}
        selectedId={selectedItemId}
        onSelect={onSelectItem}
        ariaLabel={t('analytics.topItems.aria', { measure: measureLabel })}
        shareLabel={t('analytics.topItems.shareOfAll')}
        // The share column divides by the active measure's period total, so it
        // is a mixed figure exactly when that total is one.
        {...(inItemUnits ? { shareNote: <MixedUnitsNote className="items-end" /> } : {})}
        selectLabel={(name) => t('analytics.topItems.select', { name })}
        emptyLabel={t('analytics.topItems.empty')}
      />
    </PanelSection>
  );
}
